/**
 * 受注した見積の画面の{deliveryNote}（#266、docs/96 v0.2 の起こし方1・Figma ①）。
 *   useQuoteDelivery：残（GET /estimates/:id/delivery-state）を読み、見出しのチップ（「納品 残 n行」／「納品済」）と操作
 *   （{deliveryNote}作成・下書き表示・請求書作成＝束ね請求・{deliveryNote}一覧へ）を返す。見積から直接請求した受注・分割請求の受注は使わない（active=false）
 *   FromQuoteDialog：明細の表（外す・No・名称・単位・見積の数量・納品済み・今回の数量＝既定は残）→「作成」で下書きへ。
 *   見出し・文章の行は外せない（下に入れる行が無い見出しは入れない）。率・値引き・別途の行は数量の欄が無く、外すかだけ（まだ載っていなければ入れる）
 */
import { ACCEPTANCE_MSG, DELIVERY_NOTE_MSG, type DeliveryNote, type DeliveryState, type DeliveryStateLine } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { api, ApiError, messageOf } from "../../api.js";
import { useMe } from "../../auth.js";
import { notifyError } from "../../components/Notice.jsx";
import { uses } from "../../core/uses.js";
import { DateInput } from "../../components/DateInput.jsx";
import type { HeadAction } from "../../components/HeadActions.jsx";
import { ErrorText, Field } from "../../components/ui.jsx";
import { t, useLabels } from "../../labels.js";
import { fq, toHalf, todayJst, yen } from "../../lib/num.js";
import { BundleInvoiceDialog } from "./BundleInvoiceDialog.jsx";

type QuoteHead = { id: string; documentNo: string | null; subject: string; clientName: string; honorific: string; siteAddress: string; groupId: string | null };

export function useQuoteDelivery({ quote, enabled, onDirectInvoice }: { quote: QuoteHead; enabled: boolean; onDirectInvoice: () => void }) {
  const st = useQuery({ queryKey: ["delivery-state", quote.id], queryFn: () => api<DeliveryState>("GET", `/estimates/${quote.id}/delivery-state`), enabled });
  const acceptanceOn = uses(useMe().data, "acceptance");
  const navTo = useNavigate();
  const makeAcceptance = async () => {
    try {
      const a = await api<{ id: string }>("POST", "/acceptances", { fromQuoteId: quote.id });
      navTo(`/acceptances/${a.id}`);
    } catch (e) {
      notifyError(messageOf(e));
    }
  };
  const [open, setOpen] = useState(false);
  const [bundle, setBundle] = useState(false);
  const s = st.data;
  const active = enabled && !!s && s.ordered && !s.directInvoice && !s.installments;
  const draft = s?.notes.find((x) => x.status === "draft");
  const unbilled = s?.notes.filter((x) => x.status !== "draft" && !x.invoiceId) ?? [];
  // 率・値引き・別途の行で、まだどの{deliveryNote}にも載っていないもの
  const restOthers = s?.lines.filter((l) => l.quantity == null && hasAmount(l.role) && l.deliveredCount === 0).length ?? 0;
  const rest = (s?.remainingRows ?? 0) + restOthers;
  const done = !!s && s.notes.some((x) => x.status !== "draft") && rest === 0;
  const actions: HeadAction[] = !active || !s
    ? []
    : [
        ...(draft
          ? [{ label: `${t(DELIVERY_NOTE_MSG.title)}下書き表示`, to: `/delivery-notes/${draft.id}`, testId: "dn-draft-open", rank: 1 }]
          : rest > 0
            ? [{ label: t(DELIVERY_NOTE_MSG.create), onSelect: () => setOpen(true), testId: "dn-from-quote", rank: 1 }]
            : []),
        // {deliveryNote}がある受注は{deliveryNote}から請求する（見積からの請求書は作らない）
        ...(unbilled.length
          ? [{ label: DELIVERY_NOTE_MSG.makeInvoice, onSelect: () => setBundle(true), testId: "dn-quote-bill", rank: 2 }]
          : s.notes.length === 0
            ? [{ label: "請求書作成", onSelect: onDirectInvoice, testId: "order-invoice-new", rank: 3 }]
            : []),
        ...(s.notes.length && quote.groupId ? [{ label: `${t(DELIVERY_NOTE_MSG.list)}一覧へ`, to: `/groups/${quote.groupId}?tab=dnotes`, testId: "dn-quote-list" }] : []),
        // 検収書（#266 1b）：受注から（全行を写す）。使う機能「{acceptance}」の会社だけ
        ...(acceptanceOn ? [{ label: t(ACCEPTANCE_MSG.create), onSelect: () => void makeAcceptance(), testId: "ac-from-quote", group: "state" as const }] : []),
      ];
  const chip: ReactNode = !active || !s || !s.notes.length ? null : done ? (
    <span className="chip ai" data-testid="dn-quote-chip">
      {DELIVERY_NOTE_MSG.delivered}
    </span>
  ) : rest > 0 ? (
    <button type="button" className="chip quiet chipbtn" onClick={() => (draft ? undefined : setOpen(true))} disabled={!!draft} title={draft ? undefined : t(DELIVERY_NOTE_MSG.create)} data-testid="dn-quote-chip">
      納品 {DELIVERY_NOTE_MSG.remaining.replace("{n}", String(rest))}
    </button>
  ) : null;
  const dialogs = (
    <>
      {open && s ? <FromQuoteDialog quote={quote} state={s} onClose={() => setOpen(false)} /> : null}
      {bundle && unbilled[0] ? <BundleInvoiceDialog seed={{ id: unbilled[0].id, sourceQuote: { id: quote.id }, projectGroup: null }} onClose={() => setBundle(false)} /> : null}
    </>
  );
  return { active, actions, chip, dialogs };
}

const hasAmount = (role: string) => role === "overhead_rate" || role === "discount" || role === "excluded";

function FromQuoteDialog({ quote, state, onClose }: { quote: QuoteHead; state: DeliveryState; onClose: () => void }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const L = useLabels();
  // 表に出す行：小計は出さない（{deliveryNote}が自分で計算する）
  const rows = state.lines.filter((l) => l.role !== "subtotal");
  const [off, setOff] = useState<Set<string>>(() => new Set(rows.filter((l) => (l.role === "detail" ? !(l.remaining != null && l.remaining > 0) : hasAmount(l.role) ? l.deliveredCount > 0 : false)).map((l) => l.lineId)));
  const [qty, setQty] = useState<Record<string, string>>(() => Object.fromEntries(rows.filter((l) => l.role === "detail").map((l) => [l.lineId, fq(Math.max(0, l.remaining ?? 0))])));
  const [date, setDate] = useState(todayJst());
  const [place, setPlace] = useState(quote.siteAddress);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const qOf = (l: DeliveryStateLine) => Number(qty[l.lineId] || 0);
  const on = (l: DeliveryStateLine) => !off.has(l.lineId) && (l.role !== "detail" || qOf(l) > 0);
  const over = rows.find((l) => l.role === "detail" && on(l) && l.remaining != null && qOf(l) > l.remaining + 1e-9);
  // 見出しは、次の見出しまでに入れる行があるときだけ入れる
  const picked = rows.filter((l, i) => {
    if (l.role === "group") {
      for (let j = i + 1; j < rows.length && rows[j]!.role !== "group"; j++) if (rows[j]!.role !== "note" && on(rows[j]!)) return true;
      return false;
    }
    if (l.role === "note") return true;
    return on(l);
  });
  const counted = picked.filter((l) => l.role === "detail" || hasAmount(l.role));
  const total = picked.reduce((sum, l) => sum + (l.role === "detail" ? Math.round(qOf(l) * (l.unitPrice ?? 0)) : l.role === "excluded" ? 0 : (l.amount ?? 0)), 0);
  const create = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const n = await api<DeliveryNote>("POST", "/delivery-notes", {
        fromQuoteId: quote.id,
        lines: picked.map((l) => ({ lineId: l.lineId, quantity: l.role === "detail" ? qOf(l) : null })),
        issueDate: date || todayJst(),
        siteAddress: place,
      });
      void qc.invalidateQueries({ queryKey: ["delivery-state", quote.id] });
      void qc.invalidateQueries({ queryKey: ["delivery-notes"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      nav(`/delivery-notes/${n.id}`, { state: { fresh: true } });
    } catch (e) {
      if (e instanceof ApiError && typeof e.problem.deliveryNoteId === "string") {
        nav(`/delivery-notes/${e.problem.deliveryNoteId}`);
        return;
      }
      setBusy(false);
      setError(messageOf(e));
    }
  };
  const title = t(DELIVERY_NOTE_MSG.dialogTitle);
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="dlg dlg-wide dnq" role="dialog" aria-label={title} onKeyDown={(e) => e.key === "Escape" && !busy && onClose()} data-testid="dn-quote-dialog">
        <div className="dh">
          {title}
          <div className="dnq-sub muted">
            見積書 {quote.documentNo ?? ""}　{quote.subject}　／　{quote.clientName} {quote.honorific}
          </div>
        </div>
        <div className="db">
          <div className="dnq-head">
            <DateInput value={date} onChange={setDate} label={DELIVERY_NOTE_MSG.deliveryDate} id="dnq_date" clearable={false} testId="dnq-date" />
            <Field id="dnq_place" label={DELIVERY_NOTE_MSG.deliveryPlace} className="dnq-place">
              <input id="dnq_place" className="inp" placeholder=" " maxLength={200} value={place} onChange={(e) => setPlace(e.target.value)} data-testid="dnq-place" />
            </Field>
          </div>
          <div className="dnq-scroll">
            <table className="list dnq-table" data-testid="dnq-table">
              <thead>
                <tr>
                  <th className="c">{DELIVERY_NOTE_MSG.colExclude}</th>
                  <th className="num">No</th>
                  <th>{L.lineName}</th>
                  <th>単位</th>
                  <th className="amt">{DELIVERY_NOTE_MSG.colQuoteQty}</th>
                  <th className="amt">{DELIVERY_NOTE_MSG.colDelivered}</th>
                  <th className="amt">{DELIVERY_NOTE_MSG.colNow}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => {
                  const fixed = l.role === "group" || l.role === "note";
                  const isOff = off.has(l.lineId);
                  return (
                    <tr key={l.lineId} className={(isOff ? "is-off " : "") + (l.role === "group" ? "is-group" : "")} data-testid="dnq-row">
                      <td className="c">
                        {fixed ? null : (
                          <input
                            type="checkbox"
                            aria-label={`${l.name || `${l.no}行目`}を外す`}
                            checked={isOff}
                            disabled={busy}
                            onChange={(e) => setOff((p) => { const n = new Set(p); if (e.target.checked) n.add(l.lineId); else n.delete(l.lineId); return n; })}
                            data-testid={`dnq-off-${l.no}`}
                          />
                        )}
                      </td>
                      <td className="num">{l.no}</td>
                      <td>{l.role === "group" ? <b>{l.name}</b> : l.name}</td>
                      <td>{l.unit}</td>
                      <td className="num amt">{l.quantity == null ? "" : fq(l.quantity) || "0"}</td>
                      <td className="num amt">{l.role === "detail" ? fq(l.delivered) || "0" : hasAmount(l.role) && l.deliveredCount ? DELIVERY_NOTE_MSG.delivered : ""}</td>
                      <td className="amt">
                        {l.role === "detail" ? (
                          isOff ? (
                            <span className="muted">—</span>
                          ) : (
                            <input
                              className={"inp num dnq-qty" + (l.remaining != null && qOf(l) > l.remaining + 1e-9 ? " is-invalid" : "")}
                              type="text"
                              inputMode="decimal"
                              aria-label={`${l.name}の${DELIVERY_NOTE_MSG.colNow}`}
                              value={qty[l.lineId] ?? ""}
                              disabled={busy}
                              onChange={(e) => setQty((p) => ({ ...p, [l.lineId]: toHalf(e.target.value, "dec") }))}
                              data-testid={`dnq-qty-${l.no}`}
                            />
                          )
                        ) : hasAmount(l.role) ? (
                          <span className="muted">—</span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {rows.some((l) => hasAmount(l.role)) ? <p className="hint" style={{ margin: "8px 0 0" }}>{t(DELIVERY_NOTE_MSG.rateRowNote)}</p> : null}
          {over ? <ErrorText>{t(DELIVERY_NOTE_MSG.overRemaining).replace("{name}", over.name).replace("{rest}", fq(over.remaining ?? 0) || "0")}</ErrorText> : null}
          <ErrorText>{error}</ErrorText>
        </div>
        <div className="df">
          <span className="dnq-sum" data-testid="dnq-sum">
            {DELIVERY_NOTE_MSG.rowsNow.replace("{n}", String(counted.length))}　{DELIVERY_NOTE_MSG.totalNow} <b className="num">{yen(total)}</b> 円
          </span>
          <span className="sp" />
          <button type="button" className="btn" disabled={busy} onClick={onClose}>
            取消
          </button>
          <button type="button" className="btn primary" disabled={busy || !counted.length || !!over} onClick={() => void create()} data-testid="dnq-create">
            {DELIVERY_NOTE_MSG.create2}
          </button>
        </div>
      </div>
    </div>
  );
}
